/**
 * CSS-background provenance and byte-splice mutation.
 *
 * css-tree owns parsing. We never regenerate a stylesheet: every mutation
 * replaces only the selected Url node's byte range, preserving all unrelated
 * whitespace, comments, declarations and at-rule formatting exactly.
 */

// This module is imported by the unbundled browser graph as well as Node
// tests. A bare package specifier works in Node but bricks the canvas before
// any background can be selected, so use the browser-safe vendored ESM build.
import * as csstree from '../vendor/csstree.esm.js';

const BACKGROUND_PROPERTIES = new Set(['background', 'background-image']);
const ADDRESSABLE_AT_RULES = new Set(['media', 'supports', 'container', 'layer']);

function parseCss(css) {
  try {
    return csstree.parse(css, { positions: true });
  } catch {
    return null;
  }
}

function sectionSelectorMatches(selector, sectionId) {
  return (
    selector.includes('[data-pb-section') &&
    (selector.includes(`"${sectionId}"`) || selector.includes(`'${sectionId}'`))
  );
}

function atRuleDescriptor(node, occurrence) {
  return {
    name: String(node.name || '').toLowerCase(),
    prelude: node.prelude ? csstree.generate(node.prelude) : '',
    occurrence
  };
}

function locatorKey(locator) {
  return JSON.stringify(locator);
}

function targetLocator(target) {
  if (!target || typeof target !== 'object') return null;
  if (target.locator) return target.locator;
  if (target.targetRef?.locator) return target.targetRef.locator;
  return null;
}

function expectedFingerprint(target) {
  if (!target || typeof target !== 'object') return '';
  return String(target.expectedFingerprint || target.targetRef?.expectedFingerprint || '');
}

function urlValue(node) {
  return typeof node?.value === 'string' ? node.value.trim() : '';
}

function breakpointHint(atRules) {
  const media = atRules
    .filter((entry) => entry.name === 'media')
    .map((entry) => entry.prelude)
    .join(' and ')
    .toLowerCase();
  if (media.includes('max-width')) return 'mobile';
  if (media.includes('min-width')) return 'desktop';
  return '';
}

function collectFacts(sectionCss, sectionId) {
  const ast = parseCss(sectionCss);
  if (!ast) return [];
  const facts = [];
  const ruleOccurrences = new Map();

  function walkChildren(list, atRules) {
    if (!list) return;
    const atOccurrences = new Map();
    list.forEach((node) => {
      if (node.type === 'Atrule') {
        const name = String(node.name || '').toLowerCase();
        const prelude = node.prelude ? csstree.generate(node.prelude) : '';
        const occurrenceKey = `${name}|${prelude}`;
        const occurrence = atOccurrences.get(occurrenceKey) || 0;
        atOccurrences.set(occurrenceKey, occurrence + 1);
        const nextChain = ADDRESSABLE_AT_RULES.has(name)
          ? [...atRules, atRuleDescriptor(node, occurrence)]
          : atRules;
        walkChildren(node.block?.children, nextChain);
        return;
      }
      if (node.type !== 'Rule' || !node.prelude || !node.block?.children) return;
      const selector = csstree.generate(node.prelude);
      if (!sectionSelectorMatches(selector, sectionId)) return;

      const ruleKey = `${JSON.stringify(atRules)}|${selector}`;
      const ruleOccurrence = ruleOccurrences.get(ruleKey) || 0;
      ruleOccurrences.set(ruleKey, ruleOccurrence + 1);
      const declarationOccurrences = new Map();

      node.block.children.forEach((declaration) => {
        if (declaration.type !== 'Declaration') return;
        const property = String(declaration.property || '').toLowerCase();
        if (!BACKGROUND_PROPERTIES.has(property)) return;
        const declarationOccurrence = declarationOccurrences.get(property) || 0;
        declarationOccurrences.set(property, declarationOccurrence + 1);
        const baseLocator = {
          atRules: atRules.map((entry) => ({ ...entry })),
          selector,
          ruleOccurrence,
          property,
          declarationOccurrence
        };
        let layerOrdinal = 0;
        let foundUrl = false;
        csstree.walk(declaration.value, {
          visit: 'Url',
          enter(urlNode) {
            foundUrl = true;
            const url = urlValue(urlNode);
            const locator = { ...baseLocator, layerOrdinal };
            const isData = /^data:/i.test(url);
            facts.push({
              url,
              selector,
              media: atRules
                .filter((entry) => entry.name === 'media')
                .map((entry) => entry.prelude)
                .join(' and '),
              breakpoint: breakpointHint(atRules),
              atRules: locator.atRules,
              property,
              locator,
              key: `${sectionId}:${locatorKey(locator)}`,
              expectedFingerprint: url,
              sourceStart: urlNode.loc?.start?.offset ?? null,
              sourceEnd: urlNode.loc?.end?.offset ?? null,
              writable: !isData,
              reason: isData ? 'Data-URI backgrounds are intentionally read-only' : null
            });
            layerOrdinal += 1;
          }
        });
        const value = csstree.generate(declaration.value);
        if (!foundUrl && /(?:^|[^-\w])var\(/i.test(value)) {
          const locator = { ...baseLocator, layerOrdinal: 0 };
          facts.push({
            url: value,
            selector,
            media: atRules
              .filter((entry) => entry.name === 'media')
              .map((entry) => entry.prelude)
              .join(' and '),
            breakpoint: breakpointHint(atRules),
            atRules: locator.atRules,
            property,
            locator,
            key: `${sectionId}:${locatorKey(locator)}`,
            expectedFingerprint: value,
            sourceStart: null,
            sourceEnd: null,
            writable: false,
            reason:
              'Variable-backed backgrounds are selectable but must be edited at their token source'
          });
        }
      });
    });
  }

  walkChildren(ast.children, []);
  let writableIndex = 0;
  return facts.map((fact) => ({
    ...fact,
    index: fact.writable ? writableIndex++ : null,
    provenance: {
      domain: 'section-css',
      writable: fact.writable,
      reason: fact.reason,
      locator: fact.locator
    }
  }));
}

export function extractSectionCssBackgroundImages(sectionCss, sectionId) {
  if (typeof sectionCss !== 'string' || !sectionCss) return [];
  if (typeof sectionId !== 'string' || !sectionId) return [];
  return collectFacts(sectionCss, sectionId);
}

function resolveFact(sectionCss, sectionId, target) {
  const facts = collectFacts(sectionCss, sectionId);
  let fact = null;
  if (Number.isInteger(target) && target >= 0) {
    fact = facts.find((entry) => entry.index === target) || null;
  } else {
    const locator = targetLocator(target);
    if (locator) {
      const key = locatorKey(locator);
      fact = facts.find((entry) => locatorKey(entry.locator) === key) || null;
    }
  }
  if (!fact) return { status: 'stale', reason: 'locator-not-found', fact: null };
  const expected = expectedFingerprint(target);
  if (expected && expected !== fact.expectedFingerprint) {
    return { status: 'stale', reason: 'fingerprint-mismatch', fact };
  }
  if (!fact.writable || !Number.isInteger(fact.sourceStart) || !Number.isInteger(fact.sourceEnd)) {
    return { status: 'refused', reason: fact.reason || 'background-not-writable', fact };
  }
  return { status: 'resolved', reason: null, fact };
}

export function resolveSectionCssBackgroundImage(sectionCss, sectionId, target) {
  return resolveFact(sectionCss, sectionId, target);
}

function spliceFact(sectionCss, fact, replacement) {
  return `${sectionCss.slice(0, fact.sourceStart)}${replacement}${sectionCss.slice(fact.sourceEnd)}`;
}

export function replaceSectionCssBackgroundImage(sectionCss, sectionId, target, nextUrl) {
  if (typeof sectionCss !== 'string' || !sectionCss) return null;
  if (typeof sectionId !== 'string' || !sectionId) return null;
  if (typeof nextUrl !== 'string' || !nextUrl.trim()) return null;
  const resolution = resolveFact(sectionCss, sectionId, target);
  if (resolution.status !== 'resolved') return null;
  const replacement = `url(${JSON.stringify(nextUrl.trim())})`;
  return spliceFact(sectionCss, resolution.fact, replacement);
}

export function clearSectionCssBackgroundImage(sectionCss, sectionId, target) {
  if (typeof sectionCss !== 'string' || !sectionCss) return null;
  if (typeof sectionId !== 'string' || !sectionId) return null;
  const resolution = resolveFact(sectionCss, sectionId, target);
  if (resolution.status !== 'resolved') return null;
  return spliceFact(sectionCss, resolution.fact, 'none');
}
